/* 自店番号管理画面 */
import React, { useState, useEffect, useCallback } from 'react';
import Block from '../molecules/Block';
import Text from '../molecules/Text';
import CenterButton from '../atoms/CenterButton';
import axios from '../axios';
import { handleBrowzerBack } from '../BrowzerBack';
import LoadingModal from '../molecules/LoadingModal';
import ResponseDialog from '../molecules/ResponseDialog';
import { Col, Row } from 'react-bootstrap';
import { jumpUrl } from '../Common';
import { createRoot } from 'react-dom/client';

const ShopCode: React.FC = () => {
  const [reception_store, setReceptionStore] = useState('');
  const [retailer, setRetailer] = useState('');
  const [execution_store, setExecutionStore] = useState('');
  const [is_loading, setIsLoading] = useState(false);
  const [reception_cd, setReceptionCd] = useState('');
  const [reception_id, setReceptionId] = useState('');
  const [error_message, setErrorMessage] = useState('');
  const [is_confirm, setIsConfirm] = useState(false);
  const [show_finish_dialog, setShowFinishDialog] = useState(false);

  useEffect(() => {
    // ブラウザの戻る・進む禁止
    handleBrowzerBack();
    getShopCodes();
  }, []);

  /**
   * URLから案件番号を取得する
   *
   * @returns array 案件情報
   */
  const makePathInfo = () => {
    const urls = location.pathname.split('/');
    const r_id = urls.pop();
    const r_cd = urls.pop();
    return [r_id, r_cd];
  };

  /**
   * 店舗番号を取得する
   *
   * @returns void
   */
  const getShopCodes = async () => {
    try {
      setIsLoading(true);
      const [r_id, r_cd] = makePathInfo();
      if (r_id && r_cd) {
        setReceptionId(r_id);
        setReceptionCd(r_cd);
      }
      const response = await axios.get(`/staff/set_shop/show/${r_cd}/${r_id}`);
      setReceptionStore(response.data.reception_store);
      setRetailer(response.data.retailer);
      setExecutionStore(response.data.execution_store);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 受付店の値を更新する
   */
  const handleChangereCeptionStore = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setReceptionStore(value);
    },
    [setReceptionStore]
  );

  /**
   * 取扱店の値を更新する
   */
  const handleChangereRetailer = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setRetailer(value);
    },
    [setRetailer]
  );

  /**
   * 実行店の値を更新する
   */
  const handleChangereExecutionStore = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setExecutionStore(value);
    },
    [setExecutionStore]
  );

  /**
   * 案件一覧画面に戻る
   */
  const handleBack = () => {
    location.href = `${location.origin}${process.env.MIX_MOCK_URL}/staff/list`;
  };

  /**
   * 登録押下時、バリデーションを通過させる
   */
  const handleRegist = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`/staff/set_shop/regist/${reception_cd}/${reception_id}`, {
        reception_store: reception_store,
        retailer: retailer,
        execution_store: execution_store,
      });
      setIsConfirm(response.data.result);
      setErrorMessage('');
    } catch (error) {
      // URLがある場合は遷移する
      jumpUrl(error.response.data.url);
      // バリデーション表示
      if (error.response.data.message) {
        const error_message = error.response.data.message.reduce(
          (accumulator: string, current_value: string) => `${accumulator}\n${current_value}`
        );
        setErrorMessage(error_message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 入力内容を保存する
   */
  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`/staff/set_shop/confirm/${reception_cd}/${reception_id}`, {
        reception_store: reception_store,
        retailer: retailer,
        execution_store: execution_store,
      });
      setIsLoading(false);
      setShowFinishDialog(response.data.result);
      setErrorMessage('');
      // 更新完了ダイアログを表示
    } catch (error) {
      // URLがある場合は遷移する
      jumpUrl(error.response.data.url);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="mt-5"></div>
      <Block
        label="店番設定"
        content={
          <>
            <div className="mt-3"></div>
            <div>
              <Text
                id="reception_store"
                label="受付店"
                value={reception_store}
                placeholder=""
                handleChange={handleChangereCeptionStore}
                disabled={is_confirm}
                maxlength={3}
              />
              <Text
                id="retailer"
                label="取扱店"
                value={retailer}
                placeholder=""
                handleChange={handleChangereRetailer}
                disabled={is_confirm}
                maxlength={3}
              />
              <Text
                id="execution_store"
                label="実行店"
                value={execution_store}
                placeholder=""
                handleChange={handleChangereExecutionStore}
                disabled={is_confirm}
                maxlength={3}
              />
            </div>
            {error_message && (
              <Row className="d-flex justify-content-center">
                <Col></Col>
                <Col md={9} className="alert-danger alert-danger-custom paragraph">
                  {error_message}
                </Col>
              </Row>
            )}
          </>
        }
      />
      <div className="mt-5" />
      <CenterButton
        label={is_confirm ? '確認' : '登録'}
        handleClick={is_confirm ? handleConfirm : handleRegist}
      />
      <div className="mt-5" />
      <CenterButton label={is_confirm ? 'キャンセル' : '前に戻る'} handleClick={handleBack} />
      <div className="mt-3" />
      {/* ローディング画面 */}
      <LoadingModal show={is_loading} />
      <ResponseDialog
        is_confirm={true}
        body="店番設定が完了しました"
        show={show_finish_dialog}
        title=""
        ClickCallBackConfirm={handleBack}
      />
    </>
  );
};

const container = document.getElementById('shop-code-set');
if (container) {
  const root = createRoot(container);
  root.render(<ShopCode />);
}
